import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PluginsSettingsPane from './PluginsSettingsPane';
import { usePlugins } from '../plugins/runtime/hooks';
import {
  loadSettingsFull, saveUserSettings, deleteUserSetting, SettingScope,
  ViewerOpenMode, DEFAULT_VIEWER_OPEN_MODE,
  ServerType, DEFAULT_SERVER_TYPE, DicomWebAuth,
  DEFAULT_DEEPGRAM_HOST, DEFAULT_DEEPGRAM_MODEL, DEFAULT_DEEPGRAM_ENDPOINTING,
} from '../utils/userSettings';
import { apiCall } from '../api/client';
import { refreshProvider } from '../data/providers';
import { ping as pingDicomWeb } from '../data/dicomweb';
import {
  getUserMaxSlices, setUserMaxSlices, invalidateBudget, estimateVolumeBudget,
} from '../cornerstone/volumeBudget';
import {
  DictationReplacement, DEFAULT_CLIENT_REPLACEMENTS, newReplacementId,
  loadReplacements, saveReplacements,
  encodeReplacementForDisplay, decodeReplacementForStorage,
} from '../utils/dictationReplacements';
import {
  PageFormat, DEFAULT_PAGE_FORMAT, PAPER_SIZES, matchPaperSize,
  loadPageFormat, savePageFormat,
} from '../utils/pageFormat';
import dlg from './Dialog.module.css';
import styles from './SettingsDialog.module.css';

interface Props { onClose: () => void }

interface Destination {
  destId: number;
  name: string;
  aeTitle: string;
  host: string;
  port: number;
}

interface TemplateRow {
  templateId: number;
  name: string;
  modality?: string;
  bodyPart?: string;
  updatedAt?: string;
}

type Tab = 'general' | 'server' | 'dictation' | 'reports' | 'templates' | 'dicom-send' | 'plugins';

interface ViewerOpenModeOption {
  value: ViewerOpenMode;
  title: string;
  desc: string;
}

const VIEWER_MODE_OPTIONS: ViewerOpenModeOption[] = [
  {
    value: 'modal',
    title: 'Modal in same window',
    desc: 'Viewer overlays the worklist; close (X or Esc) returns to it with all worklist state intact.',
  },
  {
    value: 'multi-tab',
    title: 'Single viewer tab',
    desc: 'Additional studies pile into one viewer tab as a multi-study tree.',
  },
  {
    value: 'separate-tab',
    title: 'Separate tab per study',
    desc: 'Every Open Study spawns its own browser tab. Useful when you have many monitors.',
  },
];

export default function SettingsDialog({ onClose }: Props) {
  const navigate = useNavigate();
  // Honor a one-shot "reopen on this tab" hint set by the caller before
  // navigating away and back (e.g. Settings > Templates > Edit > Back).
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const t = sessionStorage.getItem('settingsInitialTab') as Tab | null;
      if (t) {
        sessionStorage.removeItem('settingsInitialTab');
        return t;
      }
    } catch { /* private mode */ }
    return 'general';
  });

  // Plugin registry snapshot - drives whether the "AI Plugins" tab shows.
  const loadedPlugins = usePlugins();

  // If the user was on the plugins tab but plugins became unavailable
  // (e.g. vendor cleared the allowlist mid-session), fall back to general
  // so they don't see an empty pane.
  useEffect(() => {
    if (tab === 'plugins' && loadedPlugins.length === 0) {
      setTab('general');
    }
  }, [tab, loadedPlugins.length]);

  // Dictation
  const [deepgramKey, setDeepgramKey] = useState('');
  const [deepgramScope, setDeepgramScope] = useState<SettingScope | undefined>();
  const [showKey, setShowKey] = useState(false);
  const [scope, setScope] = useState<SettingScope>('clinic');
  // Deepgram transport settings. Blank = use the DEFAULT_DEEPGRAM_* value
  // at dictation time. Stored as clinic defaults, since latency region +
  // model choice are deployment-wide, not per-radiologist preferences.
  const [deepgramHost, setDeepgramHost] = useState('');
  const [deepgramModel, setDeepgramModel] = useState('');
  const [deepgramEndpointing, setDeepgramEndpointing] = useState<string>('');
  // AI post-process (Groq / OpenAI-compatible). OFF by default. Users
  // paste a free Groq key from console.groq.com to enable.
  const [postProcessEnabled, setPostProcessEnabled] = useState(false);
  const [postProcessEndpoint, setPostProcessEndpoint] = useState('');
  const [postProcessModel, setPostProcessModel] = useState('');
  const [postProcessKey, setPostProcessKey] = useState('');
  const [showPostProcessKey, setShowPostProcessKey] = useState(false);
  // Client-side voice replacement dictionary. Edited inline; saved with
  // the same clinic / user scope as the Deepgram key.
  const [replacements, setReplacements] = useState<DictationReplacement[]>([]);
  const [replacementsScope, setReplacementsScope] = useState<SettingScope | undefined>();

  // Report Page Setup - user's persistent Page Setup defaults. The full
  // editor via the toolbar's Page Setup dialog is where fine tuning
  // happens; this pane owns the paper size + scope + reset actions.
  const [pageFormat, setPageFormat] = useState<PageFormat>(DEFAULT_PAGE_FORMAT);
  const [pageFormatScope, setPageFormatScope] = useState<SettingScope | undefined>();

  // Templates - list-only view. Editing content routes to /template/N;
  // rename/delete happens inline; new template routes to /template/new.
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState(false);

  // DICOM destinations
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [destDraft, setDestDraft] = useState<Destination | null>(null);

  // General — viewer open mode (staged in state, persisted on Save)
  const [viewerOpenMode, setViewerOpenMode] = useState<ViewerOpenMode>(DEFAULT_VIEWER_OPEN_MODE);

  // Volume rendering — max slices per 3D volume. Persisted to localStorage
  // (browser-scoped, not synced to server settings). Empty string in the
  // input = fall back to the auto-detected budget.
  const [maxSlicesInput, setMaxSlicesInput] = useState<string>(
    getUserMaxSlices()?.toString() ?? ''
  );
  const autoBudget = estimateVolumeBudget();

  // Server — data backend config (EDGEPACS or DICOMweb)
  const [serverType, setServerType] = useState<ServerType>(DEFAULT_SERVER_TYPE);
  const [dwUrl, setDwUrl] = useState('');
  const [dwAuth, setDwAuth] = useState<DicomWebAuth>('none');
  const [dwUser, setDwUser] = useState('');
  const [dwPass, setDwPass] = useState('');
  const [dwToken, setDwToken] = useState('');
  const [dwPingState, setDwPingState] = useState<'idle' | 'running' | 'ok' | 'fail'>('idle');
  const [dwPingMsg, setDwPingMsg] = useState('');

  useEffect(() => {
    void reload();
    void loadDestinations();
    void loadTemplates();
  }, []);

  async function loadTemplates() {
    setTemplatesLoading(true);
    const res = await apiCall<TemplateRow>('templates.list', { modality: '' });
    setTemplates(res.s === 'ok' ? (res.data || []) : []);
    setTemplatesLoading(false);
  }

  async function deleteTemplate(t: TemplateRow) {
    if (!confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    const res = await apiCall('templates.delete', { templateId: t.templateId });
    if (res.s === 'ok') void loadTemplates();
    else flash(res.error || 'Delete failed', true);
  }

  function editTemplateContent(t: TemplateRow) {
    // Remember where we came from AND that the Settings dialog was open
    // on the Templates tab. TemplatePage.goBack picks up `reopen` from
    // location.state and pushes it into sessionStorage; WorklistPage
    // (or wherever this returns to) checks sessionStorage on mount and
    // reopens the Settings dialog on that tab.
    const from = window.location.pathname + window.location.hash;
    try { sessionStorage.setItem('reopenSettingsOnTab', 'templates'); } catch { /* private mode */ }
    onClose();
    navigate(`/template/${t.templateId}`, { state: { from, reopen: 'templates' } });
  }

  function newTemplate() {
    const from = window.location.pathname + window.location.hash;
    try { sessionStorage.setItem('reopenSettingsOnTab', 'templates'); } catch { /* private mode */ }
    onClose();
    navigate('/template/new', { state: { from, reopen: 'templates' } });
  }

  async function reload() {
    const s = await loadSettingsFull(true);
    setDeepgramKey(s.values.deepgramKey || '');
    setDeepgramScope(s.scope.deepgramKey);
    setDeepgramHost(s.values.deepgramHost || '');
    setDeepgramModel(s.values.deepgramModel || '');
    setDeepgramEndpointing(
      s.values.deepgramEndpointing !== undefined ? String(s.values.deepgramEndpointing) : '',
    );
    setPostProcessEnabled(!!s.values.dictationPostProcessEnabled);
    setPostProcessEndpoint(s.values.dictationPostProcessEndpoint || '');
    setPostProcessModel(s.values.dictationPostProcessModel || '');
    setPostProcessKey(s.values.dictationPostProcessKey || '');
    setViewerOpenMode((s.values.viewerOpenMode as ViewerOpenMode) || DEFAULT_VIEWER_OPEN_MODE);
    setServerType((s.values.serverType as ServerType) || DEFAULT_SERVER_TYPE);
    setDwUrl(s.values.dicomwebUrl || '');
    setDwAuth((s.values.dicomwebAuth as DicomWebAuth) || 'none');
    setDwUser(s.values.dicomwebBasicUser || '');
    setDwPass(s.values.dicomwebBasicPass || '');
    setDwToken(s.values.dicomwebBearerToken || '');
    // Voice-replacement dictionary. If the user has never saved one,
    // seed the local state with the DEFAULT_CLIENT_REPLACEMENTS so the
    // list isn't empty on first open - users can then edit or clear it.
    const r = await loadReplacements();
    setReplacements(r.replacements.length > 0 ? r.replacements : [...DEFAULT_CLIENT_REPLACEMENTS]);
    setReplacementsScope(r.scope);
    // Report Page Setup default. Same load/save pattern as everything else;
    // scope shown on the badge next to the section title.
    const pf = await loadPageFormat();
    setPageFormat(pf.format);
    setPageFormatScope(pf.scope);
    // Sync the "Clinic default vs Personal override" radio to whatever
    // scope currently owns the Deepgram key or the replacements. Without
    // this, the radio always defaults to 'clinic' on dialog open even
    // when the user's saved settings are personal - so an edit made
    // without re-toggling the radio silently writes to clinic scope
    // and the pre-existing user override then wins on next read.
    const currentScope: SettingScope | undefined = s.scope.deepgramKey || r.scope;
    if (currentScope) setScope(currentScope);
  }

  async function testDicomWebConnection() {
    setDwPingState('running');
    setDwPingMsg('');
    const res = await pingDicomWeb({
      url: dwUrl.trim().replace(/\/+$/, ''),
      auth: dwAuth,
      basicUser: dwUser,
      basicPass: dwPass,
      bearerToken: dwToken,
    });
    if (res.ok) {
      setDwPingState('ok');
      setDwPingMsg(`Connected (HTTP ${res.status})`);
    } else {
      setDwPingState('fail');
      setDwPingMsg(res.error || 'Connection failed');
    }
  }

  async function loadDestinations() {
    const res = await apiCall<Destination>('destinations.list');
    if (res.s === 'ok') setDestinations(res.data || []);
  }

  function flash(msg: string, isError = false) {
    setMessage(msg);
    setMessageError(isError);
    if (!isError) setTimeout(() => setMessage(''), 1800);
  }

  async function saveDestination() {
    if (!destDraft) return;
    if (!destDraft.name.trim() || !destDraft.aeTitle.trim()
        || !destDraft.host.trim() || !destDraft.port) {
      flash('Name, AE title, host and port are required.', true);
      return;
    }
    const res = await apiCall('destination.save', destDraft as any);
    if (res.s === 'ok') {
      setDestDraft(null);
      void loadDestinations();
      flash('Destination saved');
    } else {
      flash(res.error || 'Unable to save destination.', true);
    }
  }

  async function deleteDestination(destId: number) {
    if (!confirm('Delete this destination?')) return;
    await apiCall('destination.delete', { destId });
    void loadDestinations();
  }

  /** Persist Dictation + General. Each settings.save call covers one scope.
   *  deepgramKey honors the user's clinic/personal toggle; viewerOpenMode is
   *  always clinic-wide (a workflow choice the practice fixes once). */
  async function save() {
    setSaving(true);
    setMessage('');
    // Three separate calls because the keys live at different scopes:
    //   - deepgramKey honors the user's clinic/user toggle above
    //   - viewerOpenMode is intentionally clinic-wide (workflow choice)
    //   - server config is clinic-wide too (everyone connects to the
    //     same backend); URL + auth are practice-level infrastructure.
    const okDg = await saveUserSettings({ deepgramKey: deepgramKey.trim() }, scope);
    // Voice-replacement dictionary uses the same clinic/user scope as the
    // Deepgram key - if the user picked "Personal override" for their
    // key, their dictionary is personal too.
    const okRep = await saveReplacements(
      // Drop half-typed rows with no spoken word so a blank line the
      // user forgot about doesn't get shipped to the backend.
      replacements.filter((r) => r.spoken.trim() !== ''),
      scope,
    );
    const okVm = await saveUserSettings({ viewerOpenMode }, 'clinic');
    const okSrv = await saveUserSettings({
      serverType,
      dicomwebUrl: dwUrl.trim().replace(/\/+$/, ''),
      dicomwebAuth: dwAuth,
      dicomwebBasicUser: dwUser,
      dicomwebBasicPass: dwPass,
      dicomwebBearerToken: dwToken,
    }, 'clinic');
    // Deepgram transport settings — clinic-wide. Blank = fall back to the
    // compiled-in defaults (India host, nova-3-medical, 300ms endpointing).
    const epNum = Number(deepgramEndpointing);
    const okDgCfg = await saveUserSettings({
      deepgramHost: deepgramHost.trim() || undefined,
      deepgramModel: deepgramModel.trim() || undefined,
      deepgramEndpointing: Number.isFinite(epNum) && deepgramEndpointing !== '' ? epNum : undefined,
    }, 'clinic');
    // AI post-process (opt-in). Clinic scope — the Groq key, like the
    // Deepgram key, is a deployment credential.
    const okPp = await saveUserSettings({
      dictationPostProcessEnabled: postProcessEnabled,
      dictationPostProcessEndpoint: postProcessEndpoint.trim() || undefined,
      dictationPostProcessModel: postProcessModel.trim() || undefined,
      dictationPostProcessKey: postProcessKey.trim() || undefined,
    }, 'clinic');
    setSaving(false);
    if (okDg && okRep && okVm && okSrv && okDgCfg && okPp) {
      flash(`Saved (${scope})`);
      await reload();
      // Re-resolve the active provider so the next worklist search /
      // viewer open uses the new server config without a page reload.
      // force=true bypasses the settings cache; saveUserSettings already
      // invalidates it but be explicit here in case of timing edges.
      await refreshProvider(true);
    } else {
      flash('Save failed', true);
    }
  }

  async function revertPersonal() {
    if (!confirm('Remove your personal Deepgram key and fall back to the clinic default?')) return;
    const ok = await deleteUserSetting('deepgramKey', 'user');
    if (ok) {
      await reload();
      flash('Reverted to clinic default');
    }
  }

  return (
    <div className={dlg.overlay} onClick={onClose}>
      <div
        className={`${dlg.dialog} ${styles.dialog}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={dlg.header}>
          <h3 className={dlg.title}>Settings</h3>
          <button className={dlg.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.layout}>
          <nav className={styles.nav}>
            {(() => {
              // Hide "AI Plugins" tab when no plugins loaded. Vendor-gated
              // deployments without any licensed plugins should see no AI
              // UI at all. The pilot (Medtatvaa) has 2 plugins loaded so
              // the tab appears for them.
              const hasPlugins = loadedPlugins.length > 0;
              const tabs: Array<[Tab, string]> = [
                ['general',    'General'],
                ['server',     'Server'],
                ['dictation',  'Dictation'],
                ['reports',    'Reports'],
                ['templates',  'Templates'],
                ['dicom-send', 'DICOM Send'],
              ];
              if (hasPlugins) tabs.push(['plugins', 'AI Plugins']);
              return tabs;
            })().map(([id, label]) => (
              <button
                key={id}
                className={`${styles.navItem} ${tab === id ? styles.navActive : ''}`}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className={styles.content}>

            {tab === 'general' && (
              <>
                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Viewer behaviour</h4>
                  <p className={styles.sectionHelp}>
                    How clicking a study in the worklist opens the viewer.
                  </p>
                  <div className={styles.optionList}>
                    {VIEWER_MODE_OPTIONS.map((opt) => {
                      const selected = viewerOpenMode === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`${styles.optionCard} ${selected ? styles.optionCardActive : ''}`}
                        >
                          <input
                            type="radio"
                            name="viewerOpenMode"
                            className={styles.optionRadio}
                            checked={selected}
                            onChange={() => setViewerOpenMode(opt.value)}
                          />
                          <div className={styles.optionContent}>
                            <div className={styles.optionTitle}>{opt.title}</div>
                            <div className={styles.optionDesc}>{opt.desc}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </section>

                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Volume rendering (MPR / MIP / 3D)</h4>
                  <p className={styles.sectionHelp}>
                    Maximum number of slices used to build a 3D volume from a
                    series. Bigger series get windowed down to this cap. Higher
                    values give more anatomy in one volume; too high can freeze
                    a weaker GPU. Auto-detected default for this machine:{' '}
                    <strong>{autoBudget.maxSlices}</strong>{' '}
                    <span style={{ opacity: 0.7 }}>({autoBudget.reason})</span>.
                    Leave blank to use the default.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min={16}
                      max={2048}
                      step={16}
                      placeholder={String(autoBudget.maxSlices)}
                      value={maxSlicesInput}
                      onChange={(e) => setMaxSlicesInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseInt(maxSlicesInput, 10);
                        if (Number.isFinite(parsed) && parsed > 0) {
                          setUserMaxSlices(parsed);
                        } else {
                          setUserMaxSlices(null);
                          setMaxSlicesInput('');
                        }
                        invalidateBudget();
                      }}
                      style={{ width: 120, padding: '6px 8px' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setUserMaxSlices(null);
                        setMaxSlicesInput('');
                        invalidateBudget();
                      }}
                    >
                      Reset to default
                    </button>
                  </div>
                  <p className={styles.sectionHelp} style={{ marginTop: 8 }}>
                    Takes effect the next time a series is opened in a volume
                    mode. Persisted per browser.
                  </p>
                </section>
              </>
            )}

            {tab === 'server' && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Image Server</h4>
                <p className={styles.sectionHelp}>
                  Choose where the viewer fetches studies and image data from.
                  EDGEPACS pairs with the bundled image service; DICOMweb works
                  with any QIDO/WADO-compliant server (Orthanc, dcm4chee,
                  AWS HealthImaging, Google Healthcare API, …).
                </p>

                <div className={styles.optionList}>
                  <label
                    className={`${styles.optionCard} ${serverType === 'edgepacs' ? styles.optionCardActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="serverType"
                      className={styles.optionRadio}
                      checked={serverType === 'edgepacs'}
                      onChange={() => setServerType('edgepacs')}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionTitle}>EDGEPACS (paired)</div>
                      <div className={styles.optionDesc}>
                        Default. Talks to the bundled image service over its
                        REST API. Full feature set (reports, annotations,
                        key images, hanging protocols).
                      </div>
                    </div>
                  </label>
                  <label
                    className={`${styles.optionCard} ${serverType === 'dicomweb' ? styles.optionCardActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="serverType"
                      className={styles.optionRadio}
                      checked={serverType === 'dicomweb'}
                      onChange={() => setServerType('dicomweb')}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionTitle}>DICOMweb (standards-based)</div>
                      <div className={styles.optionDesc}>
                        QIDO-RS for search, WADO-RS for image data. Viewing only
                        in this release — reports / annotations / key images are
                        EDGEPACS-only for now.
                      </div>
                    </div>
                  </label>
                </div>

                {serverType === 'dicomweb' && (
                  <>
                    <div className={styles.field} style={{ marginTop: 16 }}>
                      <label className={styles.sectionTitle} style={{ fontSize: 12 }}>
                        Server Base URL
                      </label>
                      <p className={styles.sectionHelp}>
                        Root URL for the DICOMweb endpoints. Most production
                        setups put the viewer and the PACS behind one reverse
                        proxy (nginx, Caddy, IIS) and point this at a relative
                        path like <code>/dicom-web</code> — that avoids CORS
                        entirely. Absolute URLs work too if your PACS sends
                        the right CORS headers for this viewer's origin.
                        Trailing slash trimmed automatically.
                      </p>
                      <input
                        className={dlg.input}
                        placeholder="https://your-pacs.example.com/dicom-web"
                        value={dwUrl}
                        onChange={(e) => setDwUrl(e.target.value)}
                      />
                    </div>

                    <div className={styles.field}>
                      <label className={styles.sectionTitle} style={{ fontSize: 12 }}>
                        Authentication
                      </label>
                      <div className={styles.optionList}>
                        {(['none', 'basic', 'bearer'] as DicomWebAuth[]).map((a) => (
                          <label
                            key={a}
                            className={`${styles.optionCard} ${dwAuth === a ? styles.optionCardActive : ''}`}
                          >
                            <input
                              type="radio"
                              name="dwAuth"
                              className={styles.optionRadio}
                              checked={dwAuth === a}
                              onChange={() => setDwAuth(a)}
                            />
                            <div className={styles.optionContent}>
                              <div className={styles.optionTitle}>
                                {a === 'none'   ? 'None'
                                 : a === 'basic' ? 'Basic Auth (username + password)'
                                                 : 'Bearer token (OAuth2 / API key)'}
                              </div>
                              <div className={styles.optionDesc}>
                                {a === 'none'   ? 'Server is on a trusted network; no Authorization header sent.'
                                 : a === 'basic' ? 'Sent as Authorization: Basic base64(user:pass) on every request.'
                                                 : 'Sent as Authorization: Bearer <token> on every request.'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {dwAuth === 'basic' && (
                      <div className={styles.editorGrid}>
                        <input
                          className={dlg.input}
                          placeholder="Username"
                          value={dwUser}
                          onChange={(e) => setDwUser(e.target.value)}
                          autoComplete="off"
                        />
                        <input
                          className={dlg.input}
                          type="password"
                          placeholder="Password"
                          value={dwPass}
                          onChange={(e) => setDwPass(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                    )}

                    {dwAuth === 'bearer' && (
                      <div className={styles.field}>
                        <input
                          className={dlg.input}
                          type="password"
                          placeholder="Paste bearer token"
                          value={dwToken}
                          onChange={(e) => setDwToken(e.target.value)}
                          style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                          autoComplete="new-password"
                        />
                      </div>
                    )}

                    <div className={styles.row} style={{ alignItems: 'center' }}>
                      <button
                        type="button"
                        className={dlg.secondaryBtn}
                        onClick={testDicomWebConnection}
                        disabled={!dwUrl.trim() || dwPingState === 'running'}
                      >
                        {dwPingState === 'running' ? 'Testing…' : 'Test Connection'}
                      </button>
                      {dwPingState === 'ok' && (
                        <span style={{ color: '#2f855a', fontSize: 12, fontWeight: 500 }}>
                          ✓ {dwPingMsg}
                        </span>
                      )}
                      {dwPingState === 'fail' && (
                        <span style={{ color: '#c53030', fontSize: 12, fontWeight: 500 }}>
                          ✗ {dwPingMsg}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </section>
            )}

            {tab === 'dictation' && (
              <>
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  Deepgram API Key
                  {deepgramScope && (
                    <span
                      className={`${styles.scopeBadge} ${
                        deepgramScope === 'user' ? styles.scopeUser : styles.scopeClinic
                      }`}
                    >
                      {deepgramScope === 'user' ? 'Personal' : 'Clinic'}
                    </span>
                  )}
                </h4>
                <p className={styles.sectionHelp}>
                  Used for voice dictation in the report editor. Create an API key at{' '}
                  <code>console.deepgram.com</code>.
                </p>

                <div className={`${styles.field} ${styles.row}`}>
                  <input
                    className={dlg.input}
                    style={{ flex: 1, marginBottom: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    type={showKey ? 'text' : 'password'}
                    value={deepgramKey}
                    onChange={(e) => setDeepgramKey(e.target.value)}
                    placeholder="Paste your Deepgram API key"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={dlg.secondaryBtn}
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                </div>

                <div className={styles.optionList}>
                  <label
                    className={`${styles.optionCard} ${
                      scope === 'clinic' ? styles.optionCardActive : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="dgScope"
                      className={styles.optionRadio}
                      checked={scope === 'clinic'}
                      onChange={() => setScope('clinic')}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionTitle}>Save for everyone (clinic default)</div>
                      <div className={styles.optionDesc}>
                        All radiologists in the practice use this key.
                      </div>
                    </div>
                  </label>
                  <label
                    className={`${styles.optionCard} ${
                      scope === 'user' ? styles.optionCardActive : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="dgScope"
                      className={styles.optionRadio}
                      checked={scope === 'user'}
                      onChange={() => setScope('user')}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionTitle}>Save for just me (personal override)</div>
                      <div className={styles.optionDesc}>
                        Overrides the clinic default for your account only.
                      </div>
                    </div>
                  </label>
                </div>

                {deepgramScope === 'user' && (
                  <button
                    className={dlg.secondaryBtn}
                    onClick={revertPersonal}
                    style={{ marginTop: 10, fontSize: 12 }}
                  >
                    Revert my override → use clinic default
                  </button>
                )}

                <h4 className={styles.sectionTitle} style={{ marginTop: 20 }}>
                  Deepgram Transport (clinic-wide)
                </h4>
                <p className={styles.sectionHelp}>
                  Leave blank to use the defaults. These are the knobs most worth
                  tuning when dictation feels slow: pick the closest regional POP,
                  the model, and how long Deepgram waits for a pause before
                  finalising. India POP cuts ~250ms off every round-trip vs the
                  US global endpoint.
                </p>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    API host
                    <input
                      className={dlg.input}
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={deepgramHost}
                      onChange={(e) => setDeepgramHost(e.target.value)}
                      placeholder={`default: ${DEFAULT_DEEPGRAM_HOST}  (api.deepgram.com = global, api.in.deepgram.com = India, api.eu.deepgram.com = EU)`}
                    />
                  </label>
                </div>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Model
                    <input
                      className={dlg.input}
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={deepgramModel}
                      onChange={(e) => setDeepgramModel(e.target.value)}
                      placeholder={`default: ${DEFAULT_DEEPGRAM_MODEL}  (also valid: nova-3, nova-2-medical, base-medical)`}
                    />
                  </label>
                </div>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Endpointing (ms)
                    <input
                      className={dlg.input}
                      type="number"
                      min={10}
                      max={5000}
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={deepgramEndpointing}
                      onChange={(e) => setDeepgramEndpointing(e.target.value)}
                      placeholder={`default: ${DEFAULT_DEEPGRAM_ENDPOINTING}  (10-5000; lower = snappier but may cut off natural pauses)`}
                    />
                  </label>
                </div>
              </section>

              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>AI punctuation cleanup (beta)</h4>
                <p className={styles.sectionHelp}>
                  OFF by default. When enabled, every final dictation chunk is sent
                  to an LLM endpoint (Groq's free Llama 3.3 70B by default) to
                  clean up punctuation and capitalization. Adds ~200-400ms per
                  utterance; useful when prosody-based auto-punctuation misses.
                  {' '}Sign up at{' '}
                  <a
                    href="https://console.groq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3b82f6' }}
                  >
                    console.groq.com
                  </a>
                  {' '}(free tier, no credit card) and generate a key at{' '}
                  <a
                    href="https://console.groq.com/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3b82f6' }}
                  >
                    console.groq.com/keys
                  </a>.
                </p>
                <div className={styles.field}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={postProcessEnabled}
                      onChange={(e) => setPostProcessEnabled(e.target.checked)}
                    />
                    Enable AI punctuation cleanup
                  </label>
                </div>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    API key
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        className={dlg.input}
                        type={showPostProcessKey ? 'text' : 'password'}
                        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, flex: 1 }}
                        value={postProcessKey}
                        onChange={(e) => setPostProcessKey(e.target.value)}
                        placeholder="gsk_..."
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPostProcessKey((v) => !v)}
                        style={{ fontSize: 11 }}
                      >
                        {showPostProcessKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </label>
                </div>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Endpoint
                    <input
                      className={dlg.input}
                      type="text"
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={postProcessEndpoint}
                      onChange={(e) => setPostProcessEndpoint(e.target.value)}
                      placeholder="default: https://api.groq.com/openai/v1/chat/completions"
                    />
                  </label>
                </div>
                <div className={styles.field}>
                  <label style={{ display: 'block', fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Model
                    <input
                      className={dlg.input}
                      type="text"
                      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={postProcessModel}
                      onChange={(e) => setPostProcessModel(e.target.value)}
                      placeholder="default: llama-3.3-70b-versatile"
                    />
                  </label>
                </div>
              </section>

              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  Voice replacements
                  {replacementsScope && (
                    <span
                      className={`${styles.scopeBadge} ${
                        replacementsScope === 'user' ? styles.scopeUser : styles.scopeClinic
                      }`}
                    >
                      {replacementsScope === 'user' ? 'Personal' : 'Clinic'}
                    </span>
                  )}
                </h4>
                <p className={styles.sectionHelp}>
                  When you speak these words during dictation, the transcript inserts
                  the replacement instead. Add medical shortcuts like <code>NAD</code>
                  → <em>no acute distress</em>, or paragraph markers like{' '}
                  <em>new paragraph</em> → two blank lines.<br />
                  Common punctuation (comma, period, question mark, colon,
                  semicolon, brackets) is already handled by Deepgram itself -
                  no need to add those here.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#4a5568', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 4px' }}>
                    <div style={{ flex: 1 }}>Spoken word / phrase</div>
                    <div style={{ flex: 2 }}>Replacement (inserted in the report)</div>
                    <div style={{ width: 28 }} />
                  </div>
                  {replacements.map((r, idx) => (
                    <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <input
                        className={dlg.input}
                        style={{ flex: 1, marginBottom: 0 }}
                        value={r.spoken}
                        placeholder="e.g. NAD"
                        onChange={(e) => {
                          const next = [...replacements];
                          next[idx] = { ...next[idx], spoken: e.target.value };
                          setReplacements(next);
                        }}
                      />
                      <input
                        className={dlg.input}
                        style={{ flex: 2, marginBottom: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                        // Show real newlines as literal "\n" / "\n\n" so the
                        // user can see and edit them in a single-line input.
                        // Reversed on save in decodeReplacementForStorage.
                        value={encodeReplacementForDisplay(r.replacement)}
                        placeholder={'e.g. no acute distress  (type \\n for a line break)'}
                        onChange={(e) => {
                          const next = [...replacements];
                          next[idx] = {
                            ...next[idx],
                            replacement: decodeReplacementForStorage(e.target.value),
                          };
                          setReplacements(next);
                        }}
                      />
                      <button
                        type="button"
                        className={dlg.secondaryBtn}
                        title="Remove this row"
                        style={{ width: 28, padding: '4px 0', color: '#c53030', borderColor: '#fc8181' }}
                        onClick={() => setReplacements(replacements.filter((_, i) => i !== idx))}
                      >×</button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={dlg.secondaryBtn}
                    onClick={() => setReplacements([
                      ...replacements,
                      { id: newReplacementId(), spoken: '', replacement: '' },
                    ])}
                  >
                    + Add row
                  </button>
                  <button
                    type="button"
                    className={dlg.secondaryBtn}
                    title="Restore the built-in default dictionary (NAD, WNL, new paragraph, ...). Your existing entries are replaced."
                    onClick={() => {
                      if (!confirm('Replace your current voice replacements with the built-in defaults?')) return;
                      setReplacements([...DEFAULT_CLIENT_REPLACEMENTS]);
                    }}
                  >
                    Reset to defaults
                  </button>
                </div>

                <p className={styles.sectionHelp} style={{ marginTop: 12, opacity: 0.8 }}>
                  Match is case-insensitive and word-bounded - saying "period"
                  triggers a replacement, but "periodic" doesn't. Replacements
                  are applied only to the FINAL transcript, so interim previews
                  may briefly show the spoken word before it's substituted.
                </p>
              </section>
              </>
            )}

            {tab === 'reports' && (
              <>
                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Report Header</h4>
                  <p className={styles.sectionHelp}>
                    Design the letterhead and patient-info grid that appears at the top of every
                    signed report. Saved as clinic default or personal override from inside the designer.
                  </p>
                  <button
                    className={dlg.secondaryBtn}
                    onClick={() => { onClose(); navigate('/settings/header'); }}
                  >
                    Open Header Designer →
                  </button>
                </section>

                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>Report Macros</h4>
                  <p className={styles.sectionHelp}>
                    Text snippets triggered by <code>\trigger</code> shortcut or by voice phrase
                    during dictation.
                  </p>
                  <button
                    className={dlg.secondaryBtn}
                    onClick={() => { onClose(); navigate('/settings/macros'); }}
                  >
                    Manage Macros →
                  </button>
                </section>

                <section className={styles.section}>
                  <h4 className={styles.sectionTitle}>
                    Report Page Setup
                    {pageFormatScope && (
                      <span
                        className={`${styles.scopeBadge} ${
                          pageFormatScope === 'user' ? styles.scopeUser : styles.scopeClinic
                        }`}
                      >
                        {pageFormatScope === 'user' ? 'Personal' : 'Clinic'}
                      </span>
                    )}
                  </h4>
                  <p className={styles.sectionHelp}>
                    Default paper size, margins, and layout used when opening a
                    NEW report or loading a template. Already-saved reports keep
                    the geometry they were saved with (matches MS Word behaviour).
                    Detailed tweaks — orientation, header/footer distance,
                    different first-page, columns — live in the toolbar's Page
                    Setup dialog inside the report editor; click{' '}
                    <strong>Save as Default</strong> in that dialog to update
                    this section.
                  </p>

                  <div className={styles.field}>
                    <label className={styles.sectionTitle} style={{ fontSize: 12 }}>Paper size</label>
                    <select
                      className={dlg.input}
                      style={{ maxWidth: 320 }}
                      value={matchPaperSize(pageFormat.pageWidth, pageFormat.pageHeight)}
                      onChange={(e) => {
                        const key = e.target.value;
                        if (key === 'custom') return;   // "custom" is just a label; edit via editor
                        const preset = PAPER_SIZES.find((p) => p.key === key);
                        if (preset) {
                          setPageFormat({
                            ...pageFormat,
                            pageWidth: preset.pageWidth,
                            pageHeight: preset.pageHeight,
                          });
                        }
                      }}
                    >
                      {PAPER_SIZES.map((p) => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                      <option value="custom">Custom ({pageFormat.pageWidth} x {pageFormat.pageHeight} pt)</option>
                    </select>
                  </div>

                  <p className={styles.sectionHelp} style={{ marginTop: 8, opacity: 0.85 }}>
                    Current defaults:{' '}
                    <strong>
                      margins {pageFormat.leftMargin}/{pageFormat.rightMargin}/{pageFormat.topMargin}/{pageFormat.bottomMargin} pt
                      (L/R/T/B, 72 pt = 1")
                    </strong>
                    {', '}
                    <strong>header/footer {pageFormat.headerDistance}/{pageFormat.footerDistance} pt</strong>
                    {', '}
                    <strong>{pageFormat.numberOfColumns} column{pageFormat.numberOfColumns === 1 ? '' : 's'}</strong>
                    {pageFormat.differentFirstPage      ? ', different first page' : ''}
                    {pageFormat.differentOddAndEvenPages ? ', different odd/even'   : ''}
                  </p>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    <button
                      type="button"
                      className={dlg.secondaryBtn}
                      onClick={async () => {
                        const preset: PageFormat = { ...DEFAULT_PAGE_FORMAT };
                        setPageFormat(preset);
                        const ok = await savePageFormat(preset, scope);
                        if (ok) { setPageFormatScope(scope); flash('Reset to US Letter, 1" margins'); }
                        else { flash('Save failed', true); }
                      }}
                    >
                      Reset to US Letter
                    </button>
                    <button
                      type="button"
                      className={dlg.secondaryBtn}
                      onClick={async () => {
                        const preset: PageFormat = { ...DEFAULT_PAGE_FORMAT, pageWidth: 595, pageHeight: 842 };
                        setPageFormat(preset);
                        const ok = await savePageFormat(preset, scope);
                        if (ok) { setPageFormatScope(scope); flash('Reset to A4, 1" margins'); }
                        else { flash('Save failed', true); }
                      }}
                    >
                      Reset to A4
                    </button>
                    <button
                      type="button"
                      className={dlg.primaryBtn}
                      onClick={async () => {
                        const ok = await savePageFormat(pageFormat, scope);
                        if (ok) { setPageFormatScope(scope); flash(`Saved (${scope})`); }
                        else { flash('Save failed', true); }
                      }}
                    >
                      Save Paper Size
                    </button>
                  </div>
                </section>
              </>
            )}

            {tab === 'templates' && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Report Templates</h4>
                <p className={styles.sectionHelp}>
                  Reusable report bodies - a full CT chest write-up, a normal MRI
                  brain, a pending-findings shell, and so on. Radiologists pick
                  one from the template dropdown in the report editor and the
                  content fills below the header.<br />
                  Templates are content-only - patient details always come from
                  the current study's header.
                </p>

                {templatesLoading && <div style={{ fontSize: 12, opacity: 0.6 }}>Loading…</div>}

                {!templatesLoading && templates.length === 0 && (
                  <div style={{
                    padding: '20px 16px',
                    background: '#f7fafc',
                    border: '1px dashed #cbd5e0',
                    borderRadius: 4,
                    color: '#4a5568',
                    fontSize: 12,
                    marginBottom: 12,
                  }}>
                    No templates yet. Create your first one below - you'll be taken
                    to a blank template editor with the header shown at the top for
                    reference.
                  </div>
                )}

                {!templatesLoading && templates.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {templates.map((t) => (
                      <div key={t.templateId} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        background: '#f7fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1a2a3a' }}>
                            {t.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>
                            {t.modality || 'any modality'}
                            {t.bodyPart ? ` · ${t.bodyPart}` : ''}
                            {t.updatedAt ? ` · updated ${t.updatedAt.split(' ')[0]}` : ''}
                          </div>
                        </div>
                        <button
                          className={dlg.secondaryBtn}
                          onClick={() => editTemplateContent(t)}
                          style={{ fontSize: 12 }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate(t)}
                          className={dlg.secondaryBtn}
                          style={{
                            fontSize: 12, color: '#c53030', borderColor: '#fc8181',
                            background: '#fff5f5',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className={dlg.primaryBtn}
                  onClick={newTemplate}
                >
                  + New Template
                </button>
              </section>
            )}

            {tab === 'dicom-send' && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Send Destinations</h4>
                <p className={styles.sectionHelp}>
                  Remote PACS or workstations that worklist's <strong>Send</strong> action can
                  C-STORE images to. Each destination can be edited or removed individually.
                </p>

                {destinations.length === 0 ? (
                  <div className={styles.destList}>
                    <div className={styles.destEmpty}>No destinations configured.</div>
                  </div>
                ) : (
                  <div className={styles.destList}>
                    {destinations.map((d) => (
                      <div key={d.destId} className={styles.destRow}>
                        <div className={styles.destInfo}>
                          <div className={styles.destName}>{d.name}</div>
                          <div className={styles.destMeta}>
                            {d.aeTitle} @ {d.host}:{d.port}
                          </div>
                        </div>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setDestDraft({ ...d })}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                          onClick={() => deleteDestination(d.destId)}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  className={dlg.secondaryBtn}
                  onClick={() =>
                    setDestDraft({ destId: 0, name: '', aeTitle: '', host: '', port: 104 })
                  }
                >
                  + Add Destination
                </button>

                {destDraft && (
                  <div className={styles.editor}>
                    <div className={styles.editorTitle}>
                      {destDraft.destId ? 'Edit Destination' : 'New Destination'}
                    </div>
                    <div className={styles.editorGrid}>
                      <input
                        className={dlg.input}
                        placeholder="Name (e.g. Main PACS)"
                        value={destDraft.name}
                        onChange={(e) => setDestDraft({ ...destDraft, name: e.target.value })}
                      />
                      <input
                        className={dlg.input}
                        placeholder="AE Title"
                        value={destDraft.aeTitle}
                        onChange={(e) => setDestDraft({ ...destDraft, aeTitle: e.target.value })}
                      />
                      <input
                        className={dlg.input}
                        placeholder="Host (IP or DNS)"
                        value={destDraft.host}
                        onChange={(e) => setDestDraft({ ...destDraft, host: e.target.value })}
                      />
                      <input
                        className={dlg.input}
                        type="number"
                        placeholder="Port"
                        value={destDraft.port || ''}
                        onChange={(e) => setDestDraft({ ...destDraft, port: Number(e.target.value) })}
                      />
                    </div>
                    <div className={styles.row} style={{ justifyContent: 'flex-end' }}>
                      <button className={dlg.secondaryBtn} onClick={() => setDestDraft(null)}>
                        Cancel
                      </button>
                      <button className={dlg.primaryBtn} onClick={saveDestination}>
                        Save Destination
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {tab === 'plugins' && <PluginsSettingsPane />}

          </div>
        </div>

        <div className={`${dlg.footer} ${styles.footer}`}>
          <span
            className={`${styles.footerMsg} ${messageError ? styles.footerMsgError : ''}`}
          >
            {message}
          </span>
          <div className={styles.row}>
            <button className={dlg.secondaryBtn} onClick={onClose}>Close</button>
            <button
              className={dlg.primaryBtn}
              onClick={save}
              disabled={saving}
              title="Save General + Dictation settings (DICOM destinations have their own Save button)"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
